import { Box, Skeleton, Typography, LinearProgress } from '@mui/material';
import { PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';
import { useDashboard } from '../DashboardContext';
import { useTranslation } from 'react-i18next';
import DashboardWidget, { DashboardSizedChart } from './DashboardWidget';

import { addMoney } from "../../../util/quantity";
const COLORS = ['#2e7d32', '#0288d1', '#ed6c02', '#d32f2f', '#7b1fa2', '#00695c', '#c2185b', '#f57c00'];

const PaymentSummaryBody = () => {
  const { paymentModes, currency } = useDashboard();
  const { t } = useTranslation();

  const formatTooltipValue = (value: number) => {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value) + ` ${currency}`;
  };

  const totalPayments = paymentModes.reduce((sum, m) => addMoney(sum, m.amount), 0);

  if (paymentModes.length === 0 || totalPayments === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 200, color: 'text.secondary' }}>
        <Typography>{t('common:dashboard.noData')}</Typography>
      </Box>
    );
  }

  return (
    <>
      <DashboardSizedChart height={220}>
        <PieChart>
          <Pie
            data={paymentModes.filter(m => m.amount > 0)}
            cx="50%"
            cy="50%"
            innerRadius={55}
            outerRadius={85}
            paddingAngle={3}
            dataKey="amount"
            nameKey="mode"
          >
            {paymentModes.filter(m => m.amount > 0).map((_, idx) => (
              <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(value: number) => formatTooltipValue(value)} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
        </PieChart>
      </DashboardSizedChart>

      <Box sx={{ mt: 2, flexShrink: 0 }}>
        {paymentModes.filter(m => m.amount > 0).map((mode, idx) => {
          const pct = totalPayments > 0 ? (mode.amount / totalPayments) * 100 : 0;
          return (
            <Box key={mode.mode} sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>{mode.mode}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {formatTooltipValue(mode.amount)} ({pct.toFixed(1)}%)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={pct}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: 'action.hover',
                  '& .MuiLinearProgress-bar': { bgcolor: COLORS[idx % COLORS.length], borderRadius: 3 },
                }}
              />
            </Box>
          );
        })}
      </Box>
    </>
  );
};

const PaymentSummary = () => {
  const { sectionLoading } = useDashboard();
  const { t } = useTranslation();

  if (sectionLoading.payments) {
    return (
      <DashboardWidget title={t('common:dashboard.payments.title')} helpKey="payments.title" chartHeight={380} fitContent>
        <Skeleton variant="circular" width={200} height={200} sx={{ mx: 'auto' }} />
      </DashboardWidget>
    );
  }

  return (
    <DashboardWidget
      title={t('common:dashboard.payments.title')}
      helpKey="payments.title"
      chartHeight={220}
      fitContent
    >
      <PaymentSummaryBody />
    </DashboardWidget>
  );
};

export default PaymentSummary;